import { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X, MapPin, DollarSign, Calendar, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { CompletedJob } from "@/pages/Portfolio";

interface PortfolioMapProps {
  jobs: CompletedJob[];
  selectedJob: CompletedJob | null;
  onSelectJob: (job: CompletedJob | null) => void;
}

export function PortfolioMap({ jobs, selectedJob, onSelectJob }: PortfolioMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [markers, setMarkers] = useState<google.maps.Marker[]>([]);
  const navigate = useNavigate();

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || typeof google === "undefined") return;

    const newMap = new google.maps.Map(mapRef.current, {
      center: { lat: 39.8283, lng: -98.5795 }, // Center of US
      zoom: 4,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      styles: [
        {
          featureType: "poi",
          elementType: "labels",
          stylers: [{ visibility: "off" }],
        },
      ],
    });

    setMap(newMap);
  }, []);

  // Add markers when jobs or map changes
  useEffect(() => {
    if (!map) return;

    // Clear existing markers
    markers.forEach((marker) => marker.setMap(null));

    if (jobs.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    const newMarkers: google.maps.Marker[] = [];

    jobs.forEach((job) => {
      if (!job.latitude || !job.longitude) return;

      const position = { lat: job.latitude, lng: job.longitude };
      bounds.extend(position);

      const marker = new google.maps.Marker({
        position,
        map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: selectedJob?.id === job.id ? "#3b82f6" : "#22c55e",
          fillOpacity: 1,
          strokeWeight: 2,
          strokeColor: "#ffffff",
          scale: selectedJob?.id === job.id ? 12 : 8,
        },
        title: job.property_address,
        zIndex: selectedJob?.id === job.id ? 100 : 1,
      });

      marker.addListener("click", () => {
        onSelectJob(job);
      });

      newMarkers.push(marker);
    });

    setMarkers(newMarkers);

    // Fit bounds to show all markers
    if (jobs.length > 0) {
      map.fitBounds(bounds);
      // Don't zoom in too much
      const listener = google.maps.event.addListener(map, "idle", () => {
        if (map.getZoom()! > 15) {
          map.setZoom(15);
        }
        google.maps.event.removeListener(listener);
      });
    }
  }, [map, jobs]);

  // Update marker styles when selection changes
  useEffect(() => {
    markers.forEach((marker) => {
      const job = jobs.find(
        (j) => j.latitude === marker.getPosition()?.lat() && j.longitude === marker.getPosition()?.lng()
      );

      if (job) {
        marker.setIcon({
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: selectedJob?.id === job.id ? "#3b82f6" : "#22c55e",
          fillOpacity: 1,
          strokeWeight: 2,
          strokeColor: "#ffffff",
          scale: selectedJob?.id === job.id ? 12 : 8,
        });
        marker.setZIndex(selectedJob?.id === job.id ? 100 : 1);
      }
    });
  }, [selectedJob, markers]);

  return (
    <div className="relative h-full">
      <div ref={mapRef} className="w-full h-full" />

      {/* Selected Job Card */}
      {selectedJob && (
        <Card className="absolute bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 shadow-xl animate-in slide-in-from-bottom-4 duration-200">
          <div className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold truncate">{selectedJob.owner_name}</h3>
                <p className="text-sm text-muted-foreground truncate">
                  {selectedJob.property_address}
                </p>
                <p className="text-xs text-muted-foreground">
                  {selectedJob.property_city}, {selectedJob.property_state}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="flex-shrink-0"
                onClick={() => onSelectJob(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {selectedJob.photo_url && (
              <img
                src={selectedJob.photo_url}
                alt="Project"
                className="w-full h-32 object-cover rounded-lg mb-3"
              />
            )}

            <div className="flex flex-wrap gap-2 mb-3">
              {selectedJob.roof_type && (
                <Badge variant="secondary">{selectedJob.roof_type}</Badge>
              )}
              {selectedJob.estimated_value && (
                <Badge variant="outline" className="gap-1">
                  <DollarSign className="w-3 h-3" />
                  {selectedJob.estimated_value.toLocaleString()}
                </Badge>
              )}
              <Badge variant="outline" className="gap-1">
                <Calendar className="w-3 h-3" />
                {new Date(selectedJob.updated_at).toLocaleDateString()}
              </Badge>
            </div>

            <div className="flex gap-2">
              <Button
                size="sm"
                className="flex-1"
                onClick={() => navigate(`/jobs/${selectedJob.id}`)}
              >
                View Job
                <ExternalLink className="w-3 h-3 ml-1" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="gap-1"
              >
                <MapPin className="w-3 h-3" />
                Push to PMI
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Job count badge */}
      <div className="absolute top-4 left-4">
        <Badge variant="secondary" className="text-sm gap-1 shadow-lg">
          <MapPin className="w-3 h-3" />
          {jobs.length} Projects
        </Badge>
      </div>
    </div>
  );
}
